import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Plus, Minus, Eye, EyeOff, CreditCard, Banknote } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { WalletCard, CreateWalletModal, WithdrawModal, CashWithdrawalModal } from '@/components/walletpage'
import { AddMoneyModal } from '@/components/dashboardpage'
import { TransactionList } from '@/components/transactionspage'
import { useWallets } from '@/hooks/useWallets'
import { formatCurrency } from '@/lib/utils'
import { useQuery, useQueryClient } from 'react-query'
import { transactionApi } from '@/services/api'

export function WalletPage() {
  const { t } = useTranslation()
  const [showBalance, setShowBalance] = useState(true)
  const [activeTab, setActiveTab] = useState<'wallets' | 'transactions'>('wallets')
  const [isAddMoneyModalOpen, setIsAddMoneyModalOpen] = useState(false)
  const [isCreateWalletModalOpen, setIsCreateWalletModalOpen] = useState(false)
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false)
  const [isCashWithdrawModalOpen, setIsCashWithdrawModalOpen] = useState(false)

  const { wallets, isLoading, totalBalance, userCurrency, refetch } = useWallets()
  const queryClient = useQueryClient()

  // Fetch transactions
  const { data: transactions, isLoading: transactionsLoading } = useQuery(
    ['wallet-transactions', { limit: 50 }],
    async () => {
      try {
        const response = await transactionApi.getTransactions({ limit: 50 })
        
        // Handle paginated response from Django REST Framework
        // Format: { results: [...], count: ..., next: ..., previous: ... }
        // Or: { data: { results: [...] } }
        // Or: { data: [...] }
        let transactionsData = null
        const data = response.data as any // Type assertion for paginated response
        
        if (data) {
          // Check for paginated results (Django REST Framework format)
          if (data.results && Array.isArray(data.results)) {
            transactionsData = data.results
          }
          // Check for nested data.results
          else if (data.data?.results && Array.isArray(data.data.results)) {
            transactionsData = data.data.results
          }
          // Check for nested data.data array
          else if (data.data && Array.isArray(data.data)) {
            transactionsData = data.data
          }
          // Check if data is directly an array
          else if (Array.isArray(data)) {
            transactionsData = data
          }
        }
        
        return transactionsData || []
      } catch (error) {
        console.error('Error fetching transactions:', error)
        return []
      }
    },
    { refetchInterval: 10000 } // Refetch every 10 seconds
  )

  const handleAddMoneySuccess = () => {
    // Refetch wallets to update balance
    refetch()
    // Invalidate transactions cache to refresh the list
    queryClient.invalidateQueries(['wallet-transactions'])
    queryClient.invalidateQueries(['recent-transactions'])
  }

  const quickActions = [
    {
      id: 'add-money',
      label: t('wallet.addMoney'),
      icon: Plus,
      color: 'bg-success',
      onClick: () => setIsAddMoneyModalOpen(true)
    },
    {
      id: 'cash-withdraw',
      label: t('wallet.withdraw'),
      icon: Banknote,
      color: 'bg-warning',
      onClick: () => setIsCashWithdrawModalOpen(true)
    },
    {
      id: 'withdraw',
      label: t('wallet.withdrawCard'),
      icon: Minus,
      color: 'bg-warning',
      onClick: () => setIsWithdrawModalOpen(true)
    }
  ]

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">{t('wallet.title')}</h1>
            <p className="text-primary-100">{t('wallet.subtitle')}</p>
          </div>
          <button 
            onClick={() => setShowBalance(!showBalance)}
            className="p-2 rounded-full bg-white/20"
          >
            {showBalance ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
          </button>
        </div>

        {/* Total Balance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-sm rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/80 text-sm">{t('wallet.balance')}</span>
            <span className="text-white/60 text-xs">STP</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold">
              {showBalance ? formatCurrency(totalBalance, 'STN') : '••••••'}
            </span>
            <div className="flex items-center space-x-1">
              <CreditCard className="h-4 w-4 text-green-300" />
              <span className="text-green-300 text-sm">{t('wallet.active')}</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100">
        <div className="flex">
          <button
            onClick={() => setActiveTab('wallets')}
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'wallets'
                ? 'text-primary border-b-2 border-primary'
                : 'text-text-secondary'
            }`}
          >
            {t('wallet.wallets')}
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'transactions'
                ? 'text-primary border-b-2 border-primary'
                : 'text-text-secondary'
            }`}
          >
            {t('navigation.transactions')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'wallets' ? (
          <div className="space-y-6">
            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-lg font-semibold text-text-primary mb-4">
                {t('dashboard.quickActions')}
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {quickActions.map((action, index) => {
                  const IconComponent = action.icon
                  
                  return (
                    <motion.button
                      key={action.id}
                      onClick={action.onClick}
                      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center`}>
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        <span className="text-sm font-medium text-text-primary text-center">
                          {action.label}
                        </span>
                      </div>
                    </motion.button>
                  )
                })}
              </div>
            </motion.div>

            {/* Wallets List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {isLoading ? (
                <div className="space-y-3">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full" />
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded mb-2" />
                          <div className="h-3 bg-gray-200 rounded w-2/3" />
                        </div>
                        <div className="h-4 bg-gray-200 rounded w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : wallets && wallets.length > 0 ? (
                <div className="space-y-3">
                  {wallets.map((wallet: any, index: number) => (
                    <WalletCard 
                      key={wallet.id} 
                      wallet={wallet} 
                      showBalance={showBalance}
                      delay={index * 0.1}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Banknote className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-text-secondary">{t('wallet.noWallets')}</p>
                </div>
              )}
            </motion.div>
          </div>
        ) : (
          <div className="p-6">
            {transactionsLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full" />
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded mb-2" />
                        <div className="h-3 bg-gray-200 rounded w-2/3" />
                      </div>
                      <div className="h-4 bg-gray-200 rounded w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : transactions && Array.isArray(transactions) && transactions.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-text-primary">
                    {t('wallet.transactionHistory') || 'Histórico de Transações'}
                  </h2>
                  <p className="text-sm text-text-secondary mt-1">
                    {transactions.length} {transactions.length === 1 ? t('wallet.transaction') || 'transação' : t('wallet.transactions') || 'transações'}
                  </p>
                </div>
                <div className="space-y-3">
                  {transactions.map((transaction: any, index: number) => (
                    <motion.div
                      key={transaction.id || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                    >
                      <TransactionList 
                        transactions={[transaction]} 
                        showBalance={showBalance}
                      />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
              >
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Banknote className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  {t('wallet.transactionHistory')}
                </h3>
                <p className="text-text-secondary">
                  {t('wallet.transactionsWillAppear')}
                </p>
              </motion.div>
            )}
          </div>
        )}
      </div>

      {/* Add Money Modal */}
      <AddMoneyModal
        isOpen={isAddMoneyModalOpen}
        onClose={() => setIsAddMoneyModalOpen(false)}
        userCurrency={userCurrency}
        wallets={wallets}
        onSuccess={handleAddMoneySuccess}
      />

      {/* Create Wallet Modal */}
      <CreateWalletModal
        isOpen={isCreateWalletModalOpen}
        onClose={() => setIsCreateWalletModalOpen(false)}
        onSuccess={handleAddMoneySuccess}
      />

      {/* Withdraw Modal (refund to card) */}
      <WithdrawModal
        isOpen={isWithdrawModalOpen}
        onClose={() => setIsWithdrawModalOpen(false)}
        wallets={wallets}
        onSuccess={handleAddMoneySuccess}
      />

      {/* Cash Withdrawal Modal (pickup at a physical location) */}
      <CashWithdrawalModal
        isOpen={isCashWithdrawModalOpen}
        onClose={() => setIsCashWithdrawModalOpen(false)}
        wallets={wallets}
        onSuccess={handleAddMoneySuccess}
      />

    </div>
  )
}
